import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Check, SwapVert } from "@mui/icons-material";
import Layout from "components/Layout";
import useResponsive from "hooks/useResponsive";
import { managementTheme } from "components/management/managementTheme";
import CustomScrollbars from "components/CustomScrollbars";
import ClientSwitcher, {
  ClientSwitcherList,
  ClientSwitcherUser,
} from "components/botsManagement/ClientSwitcher";
import MajorJobsPanel from "containers/MajorJobsPanel";
import TaskWorkspace from "containers/TaskDetailsPage";
import SplitJobWorkspace from "containers/SplitJobWorkspace";
import {
  isSplitJobId,
  majorJobPath,
} from "components/botsManagement/split/splitRoutes";
import BotNotesPanel from "containers/BotNotesPanel";
import ConnectionsPanel from "containers/ConnectionsPanel";
import {
  useGetBackendUsersQuery,
  useLazyGetBackendUserBotConnectionsQuery,
} from "features/adminUsers/adminUsers";
import { BotsManagementWsEvent, botsManagementWs } from "features/ws/botsManagementWs";
import { useViewAs } from "hooks/useViewAs";
import { isBotsOperator } from "utils/roles";
import {
  ChangeEvent,
  MouseEvent,
  UIEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import useAppNavigate from "hooks/useAppNavigate";
import { AdminVendorConnection, BackendUser } from "types/interfaces/adminUsers";

const DIALOG_PAPER_NO_SPINNER_SX = {
  "& input[type=number]": { MozAppearance: "textfield" as const },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
} as const;

/** Display the backend user's own name (first/last), falling back to the username — never the client. */
function backendUserDisplayName(
  user: Pick<BackendUser, "first_name" | "last_name" | "username">,
): string {
  const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return fullName || user.username;
}

/**
 * The orders an operator can put the user list in. Sorting has to happen on the
 * server: the list is paged by infinite scroll, so an alphabet over the pages
 * loaded so far would be a false one.
 */
const USERS_SORT_OPTIONS = [
  { value: "name_asc", label: "Name (A-Z)", sortBy: "name", sortOrder: "asc" },
  { value: "name_desc", label: "Name (Z-A)", sortBy: "name", sortOrder: "desc" },
  {
    value: "newest",
    label: "Newest first",
    sortBy: "date_joined",
    sortOrder: "desc",
  },
  {
    value: "oldest",
    label: "Oldest first",
    sortBy: "date_joined",
    sortOrder: "asc",
  },
] as const;

type UsersSortValue = (typeof USERS_SORT_OPTIONS)[number]["value"];

const DEFAULT_USERS_SORT: UsersSortValue = "name_asc";
const BOTS_MGMT_USERS_SORT_KEY = "bots_management_users_sort";
const BOTS_MGMT_RECENT_USERS_KEY = "bots_management_recent_users";
const RECENT_USERS_LIMIT = 5;

function readStoredRecentUsers(): ClientSwitcherUser[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(BOTS_MGMT_RECENT_USERS_KEY) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is ClientSwitcherUser =>
          typeof item?.id === "number" && typeof item?.label === "string",
      )
      .slice(0, RECENT_USERS_LIMIT);
  } catch {
    return [];
  }
}

function usersSortOption(value: UsersSortValue) {
  return USERS_SORT_OPTIONS.find((option) => option.value === value) ?? USERS_SORT_OPTIONS[0];
}

/** A stored value that is no longer one of the options falls back to A-Z. */
function readStoredUsersSort(): UsersSortValue {
  const stored = localStorage.getItem(BOTS_MGMT_USERS_SORT_KEY);
  return USERS_SORT_OPTIONS.some((option) => option.value === stored)
    ? (stored as UsersSortValue)
    : DEFAULT_USERS_SORT;
}

/** Shared look of the small icon buttons next to the user search. */
const panelIconButtonSx = (theme: Theme) => ({
  flexShrink: 0,
  padding: "6px",
  color: theme.palette.primary.main,
  backgroundColor: "transparent",
  transition: theme.transitions.create(["color", "background-color", "transform"], {
    duration: theme.transitions.duration.shorter,
  }),
  "&:hover": {
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.action.hover,
    transform: "scale(1.08)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "1.5rem",
  },
});

/** Sort control of the user list in the client switcher. */
function UsersSortButton({
  value,
  onChange,
}: {
  value: UsersSortValue;
  onChange: (next: UsersSortValue) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = usersSortOption(value);
  return (
    <>
      <Tooltip title={`Sort: ${active.label}`}>
        <IconButton
          onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
          aria-label="Sort users"
          sx={panelIconButtonSx}
        >
          <SwapVert />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {USERS_SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              setAnchor(null);
              if (option.value !== value) onChange(option.value);
            }}
          >
            <ListItemIcon>
              {option.value === value ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function BotsManagementPage() {
  const {
    profile: botsProfile,
    readOnly: botsReadOnly,
    basePath,
  } = useViewAs();
  const canEditBots = isBotsOperator(botsProfile) && !botsReadOnly;
  // Phone layout: one page-long scroller, and the user list opens as a bottom sheet.
  const { isCardMode: isMobile } = useResponsive();
  const navigate = useAppNavigate();
  const { id, connectionId, majorJobId, taskId, splitJobId } = useParams<{
    id?: string;
    connectionId?: string;
    majorJobId?: string;
    taskId?: string;
    splitJobId?: string;
  }>();
  const location = useLocation();
  /** True when the user is on the dedicated bot-connections screen for the selected user. */
  const isConnectionsScreen = useMemo(
    () => /\/bots-management\/\d+\/connections\/?$/.test(location.pathname),
    [location.pathname],
  );
  /** The one place the notes for DPM are written (CAL-6162). */
  const isBotNotesScreen = useMemo(
    () => /\/bots-management\/\d+\/bot-notes\/?$/.test(location.pathname),
    [location.pathname],
  );
  const routedMajorJobPk =
    majorJobId != null && majorJobId !== "" && Number.isFinite(Number(majorJobId))
      ? Number(majorJobId)
      : null;
  const selectedConnectionId =
    connectionId != null && connectionId !== "" && Number.isFinite(Number(connectionId))
      ? Number(connectionId)
      : null;
  const routedTaskId =
    taskId != null && taskId !== "" && Number.isFinite(Number(taskId))
      ? Number(taskId)
      : null;
  const routedSplitJobId = isSplitJobId(splitJobId) ? splitJobId : null;

  const [search, setSearch] = useState("");
  const [usersSort, setUsersSort] = useState<UsersSortValue>(readStoredUsersSort);
  const [usersPage, setUsersPage] = useState(1);
  const [loadedBackendUsers, setLoadedBackendUsers] = useState<BackendUser[]>([]);
  const [recentUsers, setRecentUsers] = useState<ClientSwitcherUser[]>(readStoredRecentUsers);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const BOTS_MGMT_LAST_URL_KEY = "bots_management_last_url";

  // Restore last visited URL on first mount when no id is provided.
  // Client view neither reads nor writes it: the stored URL belongs to the
  // admin's own session, and following it would leave the preview shell.
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    didRestoreRef.current = true;
    if (basePath) return;
    if (!id || id === "") {
      const saved = localStorage.getItem(BOTS_MGMT_LAST_URL_KEY);
      if (saved) {
        navigate(saved, { replace: true });
      }
    }
  }, [id, navigate, basePath]);

  // Save the current meaningful URL (including ?tab=... and other search params) whenever navigation happens
  useEffect(() => {
    if (basePath) return;
    if (id && id !== "") {
      localStorage.setItem(BOTS_MGMT_LAST_URL_KEY, location.pathname + location.search);
    }
  }, [location.pathname, location.search, id, basePath]);

  useEffect(() => {
    if (id != null && id !== "") {
      const n = Number(id);
      setSelectedUserId(Number.isFinite(n) ? n : null);
    } else {
      setSelectedUserId(null);
    }
  }, [id]);

  const activeUsersSort = usersSortOption(usersSort);
  const {
    data: backendUsersData,
    isLoading: usersLoading,
    isFetching: usersFetching,
  } = useGetBackendUsersQuery({
    page: usersPage,
    page_size: 50,
    search: search || undefined,
    sort_by: activeUsersSort.sortBy,
    sort_order: activeUsersSort.sortOrder,
  });
  const backendUsers: BackendUser[] = loadedBackendUsers;
  const hasMoreBackendUsers = Boolean(backendUsersData?.next);

  // After a page refresh the URL restores the selected user id, but that user may
  // not be on the current (empty-search) page — fetch them directly by id so the
  // workspace opens without forcing a manual re-search. Decided only once the
  // page query has answered: firing while it is still in flight raced it with
  // a second /api/admin/users/ request on every load (the user was almost
  // always on the first page anyway), and loadedBackendUsers fills one render
  // after the response, so the response itself is checked too.
  const needsUserByIdFallback =
    selectedUserId != null &&
    !usersLoading &&
    !usersFetching &&
    !loadedBackendUsers.some((u) => u.id === selectedUserId) &&
    !(backendUsersData?.results ?? []).some((u) => u.id === selectedUserId);
  const { data: selectedUserByIdData, isFetching: selectedUserByIdFetching } =
    useGetBackendUsersQuery(
      { id: selectedUserId ?? undefined, page: 1, page_size: 1 },
      { skip: !needsUserByIdFallback },
    );
  const selectedUserById =
    selectedUserByIdData?.results?.find((u) => u.id === selectedUserId) ?? null;

  const [
    loadBotConnections,
    {
      data: botConnectionsData,
      isLoading: isConnectionsLoadingInitial,
      isFetching: isConnectionsFetching,
      isError: connectionsError,
    },
  ] = useLazyGetBackendUserBotConnectionsQuery();
  const connectionsLoading = isConnectionsLoadingInitial || isConnectionsFetching;

  const selectedUser = useMemo(
    () => backendUsers.find((u) => u.id === selectedUserId) ?? selectedUserById,
    [backendUsers, selectedUserId, selectedUserById],
  );
  const botConnections: AdminVendorConnection[] = botConnectionsData?.connections ?? [];
  const selectedUserLabel = selectedUser ? backendUserDisplayName(selectedUser) : null;

  // The switcher's "Recent" section. Client view keeps the admin's list as it is.
  useEffect(() => {
    if (basePath || selectedUserId == null || !selectedUserLabel) return;
    setRecentUsers((prev) => {
      if (prev[0]?.id === selectedUserId && prev[0]?.label === selectedUserLabel) return prev;
      const next = [
        { id: selectedUserId, label: selectedUserLabel },
        ...prev.filter((user) => user.id !== selectedUserId),
      ].slice(0, RECENT_USERS_LIMIT);
      try {
        localStorage.setItem(BOTS_MGMT_RECENT_USERS_KEY, JSON.stringify(next));
      } catch {
        // Not remembering must not break the switch.
      }
      return next;
    });
  }, [basePath, selectedUserId, selectedUserLabel]);
  const usersScrollRef = useRef<HTMLDivElement | HTMLUListElement | null>(null);
  const selectedUserIdRef = useRef<number | null>(selectedUserId);
  const selectedConnectionIdRef = useRef<number | null>(selectedConnectionId);
  const refetchConnectionsRef = useRef<() => Promise<unknown>>(async () => Promise.resolve());
  useEffect(() => {
    setUsersPage(1);
  }, [search]);

  useEffect(() => {
    const nextUsers = backendUsersData?.results;
    // Nothing has answered for the current arguments yet (a new sort or a new
    // search): keep the rows on screen. Emptying the list here unresolved the
    // selected user, which blanked and remounted the whole workspace.
    if (nextUsers == null) return;
    setLoadedBackendUsers((prev) => {
      if (usersPage === 1) return nextUsers;
      const existingIds = new Set(prev.map((user) => user.id));
      return [...prev, ...nextUsers.filter((user) => !existingIds.has(user.id))];
    });
  }, [backendUsersData?.results, usersPage]);

  useEffect(() => {
    selectedUserIdRef.current = selectedUserId;
  }, [selectedUserId]);

  useEffect(() => {
    selectedConnectionIdRef.current = selectedConnectionId;
  }, [selectedConnectionId]);

  useEffect(() => {
    refetchConnectionsRef.current = async () => {
      if (selectedUserIdRef.current == null) return;
      await loadBotConnections(selectedUserIdRef.current, false);
    };
  }, [loadBotConnections]);

  useEffect(() => {
    if (selectedUserId == null) return;
    void loadBotConnections(selectedUserId, false);
  }, [selectedUserId, loadBotConnections]);

  useEffect(() => {
    // The first "connected" after the page load is not a reconnect: the
    // connections (and, on the connection view, the tasks) were just loaded
    // over REST, and refetching them again exactly when the socket opened was
    // one of the duplicates CAL-5907 measured. Only a REconnect - the socket
    // died and came back - needs a catch-up refetch.
    const hasSeenConnectedRef = { current: botsManagementWs.isConnected() };
    const unsubscribeStatus = botsManagementWs.subscribeStatus((connected) => {
      if (!connected) return;
      if (!hasSeenConnectedRef.current) {
        hasSeenConnectedRef.current = true;
        return;
      }
      refetchConnectionsRef.current();
    });
    // A busy run emits an event per status write; refetching two endpoints on
    // every one of them is where the un-debounced bot-connections traffic came
    // from. Coalesce leading-edge: the first event of a window schedules the
    // refetch, the rest ride it.
    let wsRefetchTimer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribeEvents = botsManagementWs.subscribe((payload: BotsManagementWsEvent) => {
      const task = payload.task;
      if (!task) return;
      const eventUserId = task.user_id == null ? null : Number(task.user_id);
      const selectedUser = selectedUserIdRef.current == null ? null : Number(selectedUserIdRef.current);
      if (selectedUser == null) return;
      if (eventUserId != null && eventUserId !== selectedUser) return;
      if (
        selectedConnectionIdRef.current != null &&
        task.connection_id != null &&
        Number(task.connection_id) !== Number(selectedConnectionIdRef.current)
      ) {
        return;
      }
      if (wsRefetchTimer) return;
      wsRefetchTimer = setTimeout(() => {
        wsRefetchTimer = null;
        refetchConnectionsRef.current();
      }, 1000);
    });
    return () => {
      unsubscribeStatus();
      unsubscribeEvents();
      if (wsRefetchTimer) clearTimeout(wsRefetchTimer);
    };
  }, []);

  const handleSelectUser = (userId: number) => {
    navigate(`/bots-management/${userId}`);
  };

  const handleChangeUsersSort = (next: UsersSortValue) => {
    // Reset the page here rather than in an effect: React batches both updates,
    // so the request goes out for page 1 instead of re-fetching the page the
    // old order was on. A new order also starts at its own beginning, and the
    // list shrinking back to one page would otherwise leave the scroller near
    // the bottom and pull the next page in at once.
    setUsersSort(next);
    setUsersPage(1);
    if (usersScrollRef.current) usersScrollRef.current.scrollTop = 0;
    localStorage.setItem(BOTS_MGMT_USERS_SORT_KEY, next);
  };

  const handleUsersListScroll = (event: UIEvent<HTMLElement>) => {
    const element = event.currentTarget;
    const nearBottom =
      element.scrollTop + element.clientHeight >= element.scrollHeight - 48;
    if (nearBottom && hasMoreBackendUsers && !usersFetching) {
      setUsersPage((page) => page + 1);
    }
  };

  /** Canonical vendor folder slug under S3 is users/{id}/{vendor}/data/{slug}. */
  const parseBotStoragePath = (currentPath: string, taskVendor: string) => {
    const uid = String(selectedUserId ?? "user");
    const defaultPrefix = `storage_bucket/${uid}/${taskVendor}/data`;
    const norm = (currentPath || "").trim();
    if (!norm) {
      return { prefix: defaultPrefix, folderRest: "" };
    }
    const segs = norm.split("/").filter(Boolean);
    let u: string;
    let v: string;
    let rest: string[];
    if (segs[0] === "users" && segs.length >= 3) {
      u = segs[1];
      v = segs[2];
      rest = segs.slice(3);
    } else if (segs[0] === "storage_bucket" && segs.length >= 3) {
      u = segs[1];
      v = segs[2];
      rest = segs.slice(3);
    } else {
      return { prefix: defaultPrefix, folderRest: "" };
    }
    let folderSlug = "";
    if (rest.length && rest[0] === "data") {
      folderSlug = rest.slice(1).join("/");
    } else {
      folderSlug = rest.join("/");
    }
    return { prefix: `storage_bucket/${u}/${v}/data`, folderRest: folderSlug };
  };

  const buildPathPrefix = (currentPath: string, taskVendor: string) =>
    parseBotStoragePath(currentPath, taskVendor).prefix;

  const switcherUsers = useMemo<ClientSwitcherUser[]>(
    () => backendUsers.map((user) => ({ id: user.id, label: backendUserDisplayName(user) })),
    [backendUsers],
  );
  const switcherListProps = {
    users: switcherUsers,
    recent: recentUsers,
    selectedId: selectedUserId,
    loading: usersLoading,
    search,
    onSearchChange: setSearch,
    sortControl: <UsersSortButton value={usersSort} onChange={handleChangeUsersSort} />,
    onSelect: handleSelectUser,
    onScroll: handleUsersListScroll,
    scrollRef: usersScrollRef,
  };
  const clientSwitcher = (
    <ClientSwitcher {...switcherListProps} label={selectedUserLabel} sheet={isMobile} />
  );
  const resolvingUser =
    selectedUserId != null &&
    selectedUser == null &&
    (usersLoading || usersFetching || selectedUserByIdFetching);

  const workspaceContent = selectedUser == null ? (
              resolvingUser ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                    p: { xs: 2, md: 3 },
                  }}
                >
                  <Typography color="text.secondary" sx={{ alignSelf: { xs: "stretch", md: "center" } }}>
                    Select a user to manage major jobs.
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 440,
                      height: isMobile ? "min(70vh, 560px)" : "auto",
                      flex: isMobile ? "0 0 auto" : "1 1 auto",
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <ClientSwitcherList {...switcherListProps} />
                  </Box>
                </Box>
              )
            ) : routedTaskId != null ? (
              <TaskWorkspace
                switcher={clientSwitcher}
                taskId={routedTaskId}
                userId={selectedUser.id}
                onBack={(fallbackPath, jobId) => {
                  // Return to where the task was opened from (carried in ?back=),
                  // not the browser history — side-navigation must not misroute us.
                  const raw = new URLSearchParams(location.search).get("back");
                  const target = raw ? decodeURIComponent(raw) : "";
                  // The writer stores location.pathname, so the value is
                  // already shell-qualified - compare it as such.
                  if (target.startsWith(`${basePath}/bots-management/`)) {
                    // Land with the task's job expanded when the destination
                    // is a major job workspace that does not say so already.
                    const withJob =
                      jobId != null &&
                      /\/major-job\/\d+/.test(target) &&
                      !/[?&]job=/.test(target)
                        ? `${target}${target.includes("?") ? "&" : "?"}job=${jobId}`
                        : target;
                    navigate(withJob);
                  } else if (fallbackPath) {
                    // A directly opened task link carries no ?back=; its
                    // natural home is the major job workspace with its job
                    // expanded (the per-connection jobs view is retired).
                    navigate(fallbackPath);
                  } else {
                    navigate(`/bots-management/${selectedUser.id}`);
                  }
                }}
                onManageConnections={() =>
                  navigate(`/bots-management/${selectedUser.id}/connections`)
                }
              />
            ) : routedSplitJobId != null && routedMajorJobPk != null ? (
              <SplitJobWorkspace
                key={routedSplitJobId}
                jobId={routedSplitJobId}
                userId={selectedUser.id}
                userLabel={backendUserDisplayName(selectedUser)}
                majorJobId={routedMajorJobPk}
                onBack={() => navigate(majorJobPath(selectedUser.id, routedMajorJobPk))}
              />
            ) : isBotNotesScreen ? (
              <BotNotesPanel
                switcher={clientSwitcher}
                canEdit={canEditBots}
                onBack={() => navigate(`/bots-management/${selectedUser.id}`)}
              />
            ) : isConnectionsScreen ? (
              <ConnectionsPanel
                switcher={clientSwitcher}
                userId={selectedUser.id}
                userLabel={backendUserDisplayName(selectedUser)}
                connections={botConnections}
                connectionsLoading={connectionsLoading}
                connectionsError={Boolean(connectionsError)}
                onRefreshConnections={async () => refetchConnectionsRef.current()}
                onBack={() => navigate(`/bots-management/${selectedUser.id}`)}
                canEdit={canEditBots}
              />
            ) : routedMajorJobPk != null ? (
              <MajorJobsPanel
                switcher={clientSwitcher}
                userId={selectedUser.id}
                userLabel={backendUserDisplayName(selectedUser)}
                connections={botConnections}
                connectionsLoading={connectionsLoading}
                connectionsError={Boolean(connectionsError)}
                onRefreshConnections={async () => refetchConnectionsRef.current()}
                detailMajorJobId={routedMajorJobPk}
                onExitMajorDetail={() => navigate(`/bots-management/${selectedUser.id}`)}
                onOpenConnectionsScreen={() =>
                  navigate(`/bots-management/${selectedUser.id}/connections`)
                }
                onOpenBotNotesScreen={() =>
                  navigate(`/bots-management/${selectedUser.id}/bot-notes`)
                }
              />
            ) : (
              <MajorJobsPanel
                switcher={clientSwitcher}
                userId={selectedUser.id}
                userLabel={backendUserDisplayName(selectedUser)}
                connections={botConnections}
                connectionsLoading={connectionsLoading}
                connectionsError={Boolean(connectionsError)}
                onRefreshConnections={async () => refetchConnectionsRef.current()}
                onOpenMajorDetail={(mjId) =>
                  navigate(`/bots-management/${selectedUser.id}/major-job/${mjId}`)
                }
                onOpenConnectionsScreen={() =>
                  navigate(`/bots-management/${selectedUser.id}/connections`)
                }
                onOpenBotNotesScreen={() =>
                  navigate(`/bots-management/${selectedUser.id}/bot-notes`)
                }
              />
            );

  return (
    <Layout title="Automations" content="Automations" noScroll>
      <ThemeProvider theme={managementTheme}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            "& input[type=number]": { MozAppearance: "textfield" },
            "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
          }}
        >
          {/* Touch layout: ONE scroller for the whole workspace. A panel that
              opens a scroller of its own traps the swipe at its own end,
              because overscroll-behavior stops it chaining to the page. */}
          {isMobile ? (
            <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                {workspaceContent}
              </Box>
            </CustomScrollbars>
          ) : (
            workspaceContent
          )}
        </Box>
      </ThemeProvider>
    </Layout>
  );
}
